import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Countries, EmailRegex, PhoneRegex} from '../../../constants/general-variables';
import {AuthenticationHelperService} from '../../../services/helpers/authentication-helper.service';
import {AuthenticationHttpService} from '../../../services/http-services/authentication/authentication-http.service';
import {PAGES_AND_PATHS} from '../../../constants/pages-and-paths';
import {ToastrService} from 'ngx-toastr';
import { isPlatformBrowser } from '@angular/common';
import {Meta, Title} from '@angular/platform-browser';

@Component({
  selector: 'tl-create-account',
  templateUrl: './create-account.component.html',
})
export class CreateAccountComponent implements OnInit {
  isPasswordVisible = false;
  isConfirmPasswordVisible = false;
  formTriedToSubmit = false;
  createAccountForm!: FormGroup;
  isSpinnerActive = false;
  isAreaCodeOpen =  false;
  selectedArea = Countries[0];
  protected readonly Countries = Countries;
  protected readonly PAGES_AND_PATHS = PAGES_AND_PATHS;
  constructor(private formBuilder: FormBuilder,
              private toastService: ToastrService,
              private authHelperService: AuthenticationHelperService,
              private authHttpService: AuthenticationHttpService,
              private title: Title,
              private meta: Meta,
              @Inject(PLATFORM_ID) private platformId: string) { }

  ngOnInit(): void {
    this.title.setTitle('TheLysts, List it, Live it! - Sign up Today!');
    this.meta.updateTag({name: 'description', content: 'TheLysts is a platform of recommendations with tips to facilitate your life and make it more interesting. From renowned chefs to foodies, from big brands to the new artist who wants to connect with their fans, we all meet in affinities, create opportunities and form communities. TheLysts is about connection, it turns online into onlife. A call to dare the real.'});
    this.meta.updateTag({name: 'keywords', content: 'thelysts,lyst,list,task,organizer,account,trip,suggestion,music,food,restaurants,sports,movies,films,tv series,podcasts,life style,cars,boats,app,recommendation,planner,beauty,fashion,celebrities,influencers,museums,golf,ongs'});
    this.meta.updateTag({property: 'og:title', content: 'TheLysts, List it, Live it! - Sign up Today!'});
    this.meta.updateTag({property: 'og:image', content: 'https://thelysts.com/og-image/og-image-create-your-account.jpg'});
    this.meta.updateTag({property: 'og:description', content: 'TheLysts is a platform of recommendations with tips to facilitate your life and make it more interesting. From renowned chefs to foodies, from big brands to the new artist who wants to connect with their fans, we all meet in affinities, create opportunities and form communities. TheLysts is about connection, it turns online into onlife. A call to dare the real.'});

    this.buildForm();
  }

  buildForm(): void {
    this.createAccountForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(EmailRegex)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      phone_number: ['', [Validators.required, Validators.pattern(PhoneRegex)]],
      confirmPassword: ['', [Validators.required]],
      tos: [false, Validators.requiredTrue]
    });

    this.createAccountForm.addValidators(
      this.matchFields('password', 'confirmPassword')
    );
  }

  signInWithGoogle(): void {
    if (isPlatformBrowser(this.platformId)) {
      // @ts-ignore
      google.accounts.oauth2.initTokenClient({
        client_id: '485714059683-k1mgo6t1dup3ioko2o44q0acq93hr94p.apps.googleusercontent.com',
        scope: 'profile email',
        callback: (response: any) => this.handleCredentialResponse(response)
      }).requestAccessToken();
    }
  }

  handleCredentialResponse(response: any): void {
    this.authHttpService.loginWithGoogle({token: response.access_token}).then((res) => {
      if (res.data.user.hasOwnProperty('created_now')) {
        this.authHelperService.clientLogin(
          res.data.user,
          res.data.access_token,
          PAGES_AND_PATHS.googleSignUp.pagePath
        );
      } else {
        this.authHelperService.clientLogin(
          res.data.user,
          res.data.access_token,
          PAGES_AND_PATHS.home.pagePath
        );
      }
    });
  }

  async signInWithApple(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      this.authHttpService.loginWithAppleGetRedirect().then(async r => {
        window.open(r.body.data, '_self');
      });
    }
  }

  onSubmitCreateAccountForm(): void {
    this.formTriedToSubmit = true;
    if (this.createAccountForm.valid) {
      this.isSpinnerActive = true;
      this.authHttpService.postRegister({
        ...this.createAccountForm.value,
        phone_number: this.selectedArea.phone_code + ' ' + this.createAccountForm.controls['phone_number'].value
      }).then(res => {
        this.isSpinnerActive = false;
        this.authHelperService.clientLogin(res.data.user, res.data.token, PAGES_AND_PATHS.verifyAccount.pagePath);
      }).catch(({error}: {error: { errors: { [key: string]: string }}}) => {
        this.isSpinnerActive = false;
        Object.values(error.errors).forEach(error => this.toastService.error(error));
      });
    }
  }

  onSelectCodeArea(country: any): void {
    this.selectedArea = country;
    this.isAreaCodeOpen = false;
  }

  matchFields(fieldToMatch: string, fieldToValidate: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const controlToMatch = formGroup.get(fieldToMatch);
      const controlToValidate = formGroup.get(fieldToValidate);

      if (!controlToMatch || !controlToValidate) {
        return null;
      }

      if (controlToMatch.value !== controlToValidate.value) {
        controlToValidate.setErrors({ mustMatch: true });
        return { mustMatch: true };
      } else {
        if (controlToValidate.hasError('mustMatch')) {
          controlToValidate.setErrors(null);
        }
        return null;
      }
    };
  }
}
